import { Link } from "react-router-dom";
import { ArrowRight, Check, Route as RouteIcon, CalendarCheck, UserCheck, RefreshCw, Timer } from "lucide-react";
import { Logo } from "@/components/Logo";
import Footer from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";
import SEO_DATA from "@/seo/seoPages.json";

const PAGE = SEO_DATA.pages["/repasser-permis"];
const RESERVE = "/reserver?parcours=repassage";

// Le même texte alimente la FAQ visible ET les données structurées FAQPage.
const FAQ = [
  ["Combien de temps faut-il attendre pour repasser le permis après un échec ?", "Il n'y a pas de délai minimum imposé : tout dépend des places d'examen disponibles. C'est pour ça qu'il vaut mieux passer par une auto-école qui a des dates rapides. Nos auto-écoles partenaires te proposent une date d'examen rapidement dans leurs formules courtes."],
  ["Combien d'heures de conduite faut-il pour repasser son permis ?", "Cela dépend de ton niveau, et chaque auto-école propose sa propre formule. Notre recommandation : au moins 10 heures de conduite sur environ 2 semaines, pour t'entraîner sur le parcours d'examen et corriger les points qui t'ont fait échouer."],
  ["Est-ce que je dois repasser le code si j'ai raté la conduite ?", "Non, tant que ton code est encore valable. Le code reste valable 5 ans et te permet de te présenter jusqu'à 5 fois à l'examen pratique pendant cette période."],
  ["Puis-je changer d'auto-école pour repasser mon permis ?", "Oui. Ton dossier (numéro NEPH) peut être transféré vers une nouvelle auto-école. Indique simplement ton numéro NEPH lors de ta réservation sur PlanPermis."],
  ["L'accompagnement le jour de l'examen est-il inclus ?", "Dans les formules courtes de nos auto-écoles partenaires, l'accompagnement à l'examen est inclus : tu arrives préparé sur le parcours, avec ton enseignant. Le détail exact de chaque formule est affiché avant ta réservation."],
];

export default function RepasserPermis() {
  useSeo({
    title: PAGE.title,
    description: PAGE.description,
    path: "/repasser-permis",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Article", headline: PAGE.h1, description: PAGE.description, inLanguage: "fr-FR", mainEntityOfPage: `${SEO_DATA.site}/repasser-permis`, author: { "@type": "Organization", name: "PlanPermis" } },
        { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
        { "@type": "BreadcrumbList", itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: `${SEO_DATA.site}/` },
          { "@type": "ListItem", position: 2, name: "Repasser son permis", item: `${SEO_DATA.site}/repasser-permis` },
        ] },
      ],
    },
  });

  const cta = (testId, label = "Voir les formules pour repasser") => (
    <Link to={RESERVE} data-testid={testId} className="inline-flex items-center gap-2 rounded-xl bg-brand px-7 py-4 text-base font-bold text-white shadow-lg shadow-brand/20 hover:bg-brand-hover">
      {label} <ArrowRight className="h-5 w-5" />
    </Link>
  );

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
          <Link to="/"><Logo className="h-10" /></Link>
          <Link to={RESERVE} data-testid="rp-nav-cta" className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover">Repasser vite</Link>
        </div>
      </header>

      <main className="flex-1">
        <section className="bg-ink px-6 py-14 text-white sm:px-12">
          <div className="mx-auto max-w-3xl">
            <nav className="text-xs text-neutral-400" aria-label="Fil d'Ariane"><Link to="/" className="hover:text-white">Accueil</Link> › <span className="text-neutral-200">Repasser son permis</span></nav>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-bold ring-1 ring-white/15"><RefreshCw className="h-4 w-4" /> Spécial repassage</span>
            <h1 className="mt-4 text-4xl font-black leading-tight tracking-tight sm:text-5xl" data-testid="rp-title">{PAGE.h1}</h1>
            <p className="mt-5 text-lg leading-relaxed text-neutral-300">{PAGE.intro}</p>
            <ul className="mt-6 grid gap-2.5 text-sm text-neutral-200 sm:grid-cols-3">
              {["Formules courtes en accéléré", "Date d'examen rapide", "Accompagnement inclus"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-300" />{t}</li>
              ))}
            </ul>
            <div className="mt-8">{cta("rp-hero-cta")}</div>
          </div>
        </section>

        <div className="mx-auto max-w-3xl space-y-14 px-6 py-14">
          <section>
            <h2 className="text-2xl font-black tracking-tight text-ink sm:text-3xl">Pourquoi choisir une formule accélérée courte pour repasser ?</h2>
            <ul className="mt-6 grid gap-4 sm:grid-cols-2">
              {[
                [RouteIcon, "Tu t'entraînes sur le parcours d'examen", "Ton enseignant te fait travailler sur les rues et les situations du secteur où tu passes l'examen."],
                [UserCheck, "Tu corriges ce qui t'a fait échouer", "Les heures sont ciblées sur tes points faibles : priorités, insertion, manœuvres, autonomie."],
                [Timer, "Tu gardes tes automatismes", "Plusieurs heures rapprochées, sans coupure : tu arrives à l'examen en pleine confiance."],
                [CalendarCheck, "Une date d'examen rapide", "Nos auto-écoles partenaires te proposent une date rapidement, avec l'accompagnement inclus dans les forfaits courts."],
              ].map(([Icon, t, d]) => (
                <li key={t} className="rounded-2xl border border-neutral-200 p-5">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand/10 text-brand"><Icon className="h-5 w-5" /></span>
                  <p className="mt-3 font-bold text-ink">{t}</p>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-600">{d}</p>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8" data-testid="rp-reco">
            <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Notre recommandation</p>
            <h2 className="mt-2 text-2xl font-black text-ink">Au moins 10 heures de conduite en 2 semaines</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-neutral-700">
              {"Chaque auto-école fixe le nombre d'heures de sa formule selon ton rythme d'apprentissage. Pour être prêt sur le parcours d'examen, nous recommandons au minimum 10 heures de conduite sur environ 2 semaines. Sur PlanPermis, les formules qui correspondent sont repérées automatiquement avec le badge « Idéal pour repasser »."}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black tracking-tight text-ink sm:text-3xl">Comment repasser ton permis rapidement avec PlanPermis ?</h2>
            <ol className="mt-5 space-y-3 text-[15px] text-neutral-700">
              {[
                ["Choisis ta ville", "et regarde les formules disponibles ce mois-ci : les formules courtes pour repasser sont affichées en premier."],
                ["Réserve gratuitement en 3 minutes", "en indiquant que tu as déjà passé l'examen : l'auto-école saura que c'est un repassage."],
                ["Appelle ton auto-école sous 2 jours", "pour fixer tes heures d'entraînement et ta date d'examen."],
                ["Entraîne-toi sur le parcours et repasse l'examen", "accompagné par ton enseignant."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-3"><span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand text-sm font-black text-white">{i + 1}</span><span><strong>{t}</strong> {d}</span></li>
              ))}
            </ol>
            <div className="mt-6">{cta("rp-mid-cta")}</div>
            <p className="mt-4 text-sm text-neutral-600">
              {"Tu veux changer d'auto-école pour repasser ? "}<Link to="/transfert-auto-ecole" className="font-bold text-brand hover:underline">{"Voir comment transférer ton dossier"}</Link>
              {" · "}<Link to="/permis-accelere" className="font-bold text-brand hover:underline">Tout savoir sur le permis accéléré</Link>
            </p>
          </section>

          <section data-testid="rp-faq">
            <h2 className="text-2xl font-black tracking-tight text-ink sm:text-3xl">Questions fréquentes sur le repassage du permis</h2>
            <div className="mt-4">
              {FAQ.map(([q, a]) => (
                <details key={q} className="group border-b border-neutral-200 py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between font-bold text-ink">{q}<span className="ml-4 text-xl text-brand transition-transform group-open:rotate-45">+</span></summary>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-600">{a}</p>
                </details>
              ))}
            </div>
          </section>
        </div>

        <section className="bg-brand px-6 py-14 text-center text-white">
          <h2 className="text-3xl font-black tracking-tight">Prêt à décrocher ton permis cette fois ?</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/85">Regarde les formules courtes disponibles près de chez toi. Gratuit, en 3 minutes.</p>
          <Link to={RESERVE} data-testid="rp-final-cta" className="mt-7 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-4 font-bold text-brand hover:bg-neutral-100">Voir les formules pour repasser <ArrowRight className="h-5 w-5" /></Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
